/**
 * PhotoCollection2TestPage - Interactive test page for PhotoCollection2 components
 *
 * Displays real photos from database with interactive controls.
 * Checkpoint 3: Layout and template switching controls.
 *
 * @module pages/PhotoCollection2TestPage
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Panel, PanelResizeHandle } from 'react-resizable-panels';
import { SplitPane } from '../../components/layout/SplitPane';
import { usePhotosGraphQL } from '../../graphql/hooks/usePhotosGraphQL';
import { Heading } from '../../components/atoms/Heading';
import { Text } from '../../components/atoms/Text';
import { Button } from '../../components/atoms/Button';
import { generateMockPhotoData, generateLargePhotoCollection } from '../../utils/mockPhotoData';
import { assignOrderValues } from '../../lib/utils/reorder';
import type { TemplateName, PhotoItem2ViewModel, PhotoItem2Data } from '../../components/PhotoItem2/PhotoItem2.types';
import type { LayoutName, SelectionMode, ReorderResult } from '../../components/PhotoCollection2/PhotoCollection2.types';
import type { DetailLocation } from '../../components/composed/CollectionControlPanel';
import type { Photo } from '../../types/photo';
import logger from '../../utils/logger';

// Import extracted components
import { CollectionSettings, PhotoViewerSettings, type SortCriterion } from './SettingsComponents';
import { CollectionComponentView, PhotoViewerComponentView } from './ViewComponents';

/**
 * Convert Photo to PhotoItem2ViewModel format
 * After refactoring, PhotoItem2ViewModel just wraps the Photo object
 */
function mapPhotoToPhotoItem2Data(photo: Photo): PhotoItem2ViewModel {
  // Calculate aspect ratio from photo dimensions
  let aspectRatio: number | undefined;
  if (photo.width && photo.height && photo.height > 0) {
    aspectRatio = photo.width / photo.height;
  }

  return {
    photoData: photo,
    aspectRatio,
    critiqueCount: 0,
    averageCritiqueRating: undefined,
    photographerRating: undefined,
  };
}

/**
 * Helper function to convert PhotoItem2Data to Photo type
 */
function mapPhotoItem2DataToPhoto(photoItem: PhotoItem2Data): Photo {
  return photoItem.photoData;
}

/**
 * Data source modes for edge case testing (Checkpoint 6)
 */
type DataSourceMode = 'real-10' | 'mock-0' | 'mock-10' | 'mock-500';

/**
 * 25 realistic photography critique comments with varying lengths (2-50 words)
 * Used for generating mock critique data in test scenarios
 */
const MOCK_CRITIQUE_COMMENTS: string[] = [
  'Beautiful composition!',
  'Great light.',
  'The framing could be tighter.',
  'I love the depth of field in this shot. The subject really pops against the background.',
  'Consider cropping to emphasize the main subject.',
  'Excellent use of natural light.',
  'The shadows feel harsh here.',
  'This would benefit from warmer white balance.',
  'Perfect timing capturing this moment. The expression is genuine and the background complements it beautifully without being distracting.',
  'The composition follows the rule of thirds nicely.',
  'Too much negative space on the right side.',
  'The colors are vibrant and eye-catching.',
  "I'd suggest increasing the contrast slightly to make the subject pop more.",
  "The subject's eyes are perfectly sharp. Well done with the focus.",
  'This reminds me of classic street photography from the 1960s. The grain and tones give it such character and authenticity.',
  'Try a lower angle next time for more drama.',
  'The leading lines draw the eye perfectly through the frame.',
  'Underexposed by about a stop.',
  'Fantastic detail in the textures and patterns throughout the image.',
  "The background is distracting from the main subject. Consider using a wider aperture to blur it more, or recompose to find a cleaner background that doesn't compete for attention.",
  'Nice bokeh quality here.',
  "The horizon line isn't level, which creates a sense of unease.",
  'Great use of symmetry in this architectural shot. The reflections are stunning.',
  'The motion blur adds energy to the scene and conveys movement effectively.',
  'This could be a portfolio piece with minor adjustments to the highlights and shadows in post-processing. The composition is already strong, just needs a bit of fine-tuning to really shine.',
];

/**
 * PhotoCollection2TestPage component
 *
 * Test page for validating PhotoCollection2 functionality.
 * Loads real photos from database and displays with interactive controls.
 */
export function PhotoCollection2TestPage() {
  // T036-T037: Tab state for switching between Collection and PhotoViewer tests
  const [activeTab, setActiveTab] = useState<'collection' | 'photoviewer'>('collection');
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);

  // Fetch photos from database
  const { photos: allPhotos, loading: isLoading, error } = usePhotosGraphQL();

  // Data source mode for edge case testing (Checkpoint 6)
  const [dataSourceMode, setDataSourceMode] = useState<DataSourceMode>('real-10');

  // Track the last shuffled photo IDs to prevent re-shuffling on every render
  const lastShuffledIdsRef = useRef<string[]>([]);
  const lastDataSourceModeRef = useRef<DataSourceMode>(dataSourceMode);

  // Reset photo selection and selection state when data source changes
  useEffect(() => {
    setSelectedPhotoIndex(0);
    setSelectedIds([]);
  }, [dataSourceMode]);

  // Stable empty array reference to prevent infinite loops
  const EMPTY_PHOTOS: PhotoItem2ViewModel[] = useMemo(() => [], []);

  // Memoize mock data separately - only regenerate when explicitly needed
  const mockPhotos10 = useMemo(() => {
    logger.info('CollectionTest', 'Generating mock-10 photos (one-time)');
    return generateMockPhotoData(10);
  }, []);

  const mockPhotos500 = useMemo(() => {
    logger.info('CollectionTest', 'Generating mock-500 photos (one-time)');
    return generateLargePhotoCollection(500);
  }, []);

  // Convert and select photos based on data source mode
  const realPhotosSelection = useMemo(() => {
    if (dataSourceMode !== 'real-10') return EMPTY_PHOTOS;
    if (!allPhotos || allPhotos.length === 0) return EMPTY_PHOTOS;

    const startTime = performance.now();

    const shouldReshuffle =
      lastDataSourceModeRef.current !== dataSourceMode ||
      lastShuffledIdsRef.current.length === 0;

    let result: PhotoItem2ViewModel[];

    if (shouldReshuffle) {
      const shuffled = [...allPhotos];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      const selectedPhotos = shuffled.slice(0, 10);
      lastShuffledIdsRef.current = selectedPhotos.map(p => p.id);
      lastDataSourceModeRef.current = dataSourceMode;

      result = selectedPhotos.map(mapPhotoToPhotoItem2Data);
    } else {
      const photoMap = new Map(allPhotos.map(p => [p.id, p]));
      result = lastShuffledIdsRef.current
        .map(id => photoMap.get(id))
        .filter((p): p is Photo => p !== undefined)
        .map(mapPhotoToPhotoItem2Data);
    }

    // Deduplicate by thumbnail URL
    const seen = new Set<string | number>();
    result = result.filter(photo => {
      const thumbnailUrl = photo.photoData.variants?.find(v => v.variantType === 'thumbnail')?.storageUrl
        || photo.photoData.variants?.[0]?.storageUrl;
      const key = thumbnailUrl || String(photo.photoData.id);
      if (seen.has(key)) {
        logger.warn('CollectionTest', `Skipping duplicate image: ${photo.photoData.id}, URL: ${key}`);
        return false;
      }
      seen.add(key);
      return true;
    });

    const endTime = performance.now();
    logger.info('CollectionTest', `After deduplication: ${result.length} unique photos in ${(endTime - startTime).toFixed(2)}ms`);

    return result;
  }, [allPhotos, dataSourceMode, EMPTY_PHOTOS]);

  // Select the appropriate photo source based on data source mode
  const photosWithoutCritiques = useMemo(() => {
    switch (dataSourceMode) {
      case 'mock-0':
        return EMPTY_PHOTOS;
      case 'mock-10':
        return mockPhotos10;
      case 'mock-500':
        return mockPhotos500;
      case 'real-10':
      default:
        return realPhotosSelection;
    }
  }, [dataSourceMode, EMPTY_PHOTOS, mockPhotos10, mockPhotos500, realPhotosSelection]);

  // Add mock critique data to photos for feed templates
  const photos = useMemo(() => {
    function createSeededRandom(seed: number) {
      let state = seed;
      return (min: number, max: number): number => {
        state = (state * 1103515245 + 12345) % 2147483648;
        const normalized = state / 2147483648;
        return Math.floor(normalized * (max - min + 1)) + min;
      };
    }

    function photoIdToSeed(photoId: string | number): number {
      if (typeof photoId === 'string') {
        let hash = 0;
        for (let i = 0; i < photoId.length; i++) {
          hash = ((hash << 5) - hash) + photoId.charCodeAt(i);
          hash = hash & hash;
        }
        return Math.abs(hash);
      }
      return photoId;
    }

    return photosWithoutCritiques.map((photo) => {
      const seed = photoIdToSeed(photo.photoData.id);
      const random = createSeededRandom(seed);
      const critiqueCount = random(0, 10);

      const critiques = [];
      for (let i = 0; i < critiqueCount; i++) {
        const daysAgo = random(0, 30);
        const createdAt = new Date();
        createdAt.setDate(createdAt.getDate() - daysAgo);
        const commentIndex = random(0, MOCK_CRITIQUE_COMMENTS.length - 1);

        critiques.push({
          id: `mock-${photo.photoData.id}-${i}`,
          photoId: photo.photoData.id,
          collectionId: null,
          userId: `mock-user-${random(1, 9)}`,
          username: `user${random(0, 9)}`,
          displayName: `User ${random(0, 9)}`,
          content: MOCK_CRITIQUE_COMMENTS[commentIndex],
          rating: random(1, 10) <= 7 ? random(4, 5) : random(1, 3),
          parentId: null,
          threadDepth: 0,
          createdAt: createdAt.toISOString(),
        });
      }

      return { ...photo, critiques };
    });
  }, [photosWithoutCritiques]);

  // Layout control state
  const [layout, setLayout] = useState<LayoutName>('grid');
  const [itemTemplate, setItemTemplate] = useState<TemplateName>('photo-only');
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('none');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [zoom, setZoom] = useState<number>(1.0);
  const [detailLocation, setDetailLocation] = useState<DetailLocation>('none');
  const [reorderMode, setReorderMode] = useState<boolean>(false);
  const [showFixedHeader, setShowFixedHeader] = useState<boolean>(false);
  const [showScrollHeader, setShowScrollHeader] = useState<boolean>(false);
  const [showScrollFooter, setShowScrollFooter] = useState<boolean>(false);
  const [showFixedFooter, setShowFixedFooter] = useState<boolean>(false);
  const [reorderablePhotos, setReorderablePhotos] = useState<PhotoItem2ViewModel[]>([]);
  const [activeSortCriterion, setActiveSortCriterion] = useState<SortCriterion>('none');
  const lastProcessedPhotosRef = useRef<typeof photos | null>(null);

  // Initialize reorderable photos with order values when photos change
  useEffect(() => {
    if (lastProcessedPhotosRef.current === photos) return;
    lastProcessedPhotosRef.current = photos;

    logger.info('CollectionTest', `Photos changed, length: ${photos.length}`);

    if (photos.length > 0) {
      const sourceIds = photos.map(p => p.photoData.id);
      const uniqueSourceIds = new Set(sourceIds);
      logger.info('CollectionTest', `Source photos - unique IDs: ${uniqueSourceIds.size}, total: ${sourceIds.length}`);

      if (uniqueSourceIds.size !== sourceIds.length) {
        logger.error('CollectionTest', 'DUPLICATE IDs IN SOURCE PHOTOS!');
        const duplicates = sourceIds.filter((id, index) => sourceIds.indexOf(id) !== index);
        logger.error('CollectionTest', 'Duplicate IDs:', [...new Set(duplicates)]);
      }

      const photosWithOrder = assignOrderValues<PhotoItem2ViewModel>(photos);
      setReorderablePhotos(photosWithOrder);

      if (activeSortCriterion !== 'none') {
        setActiveSortCriterion('none');
      }
    } else {
      setReorderablePhotos(prev => prev.length === 0 ? prev : []);
    }
  }, [photos]);

  // Sort functions
  const sortByDate = useCallback((items: PhotoItem2ViewModel[], direction: 'asc' | 'desc') => {
    return [...items].sort((a, b) => {
      const dateA = a.photoData.metadata?.capture?.dateTimeOriginal ? new Date(a.photoData.metadata.capture.dateTimeOriginal).getTime() : 0;
      const dateB = b.photoData.metadata?.capture?.dateTimeOriginal ? new Date(b.photoData.metadata.capture.dateTimeOriginal).getTime() : 0;
      return direction === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, []);

  const sortByTitle = useCallback((items: PhotoItem2ViewModel[], direction: 'asc' | 'desc') => {
    return [...items].sort((a, b) => {
      const titleA = a.photoData.description || String(a.photoData.id);
      const titleB = b.photoData.description || String(b.photoData.id);
      const comparison = titleA.localeCompare(titleB);
      return direction === 'asc' ? comparison : -comparison;
    });
  }, []);

  // Apply sort to photos and reassign order values
  const applySortCriterion = useCallback((criterion: SortCriterion) => {
    setActiveSortCriterion(criterion);
    setReorderablePhotos(currentPhotos => {
      logger.info('CollectionTest', `Applying criterion: ${criterion}, current photos count: ${currentPhotos.length}`);

      if (criterion === 'none') {
        const sorted = [...currentPhotos].sort((a, b) => {
          const orderA = (a as any).order || 0;
          const orderB = (b as any).order || 0;
          return orderA - orderB;
        });
        return sorted;
      }

      let sorted: PhotoItem2ViewModel[];
      if (criterion === 'date-asc') {
        sorted = sortByDate(currentPhotos, 'asc');
      } else if (criterion === 'date-desc') {
        sorted = sortByDate(currentPhotos, 'desc');
      } else if (criterion === 'title-asc') {
        sorted = sortByTitle(currentPhotos, 'asc');
      } else {
        sorted = sortByTitle(currentPhotos, 'desc');
      }

      const result = assignOrderValues<PhotoItem2ViewModel>(sorted);
      const ids = new Set(result.map(p => p.photoData.id));
      logger.info('CollectionTest', `Sorted result - unique IDs: ${ids.size}, total items: ${result.length}`);

      if (ids.size !== result.length) {
        logger.error('CollectionTest', 'DUPLICATE IDs DETECTED! Filtering...');
        const seen = new Set<string>();
        return result.filter(p => {
          if (seen.has(p.photoData.id)) return false;
          seen.add(p.photoData.id);
          return true;
        });
      }

      return result;
    });
  }, [sortByDate, sortByTitle]);

  // Handle reorder callback
  const handleReorder = useCallback((result: ReorderResult) => {
    logger.info('CollectionTest', 'Reorder:', {
      movedItem: result.movedItem.photoData.id,
      oldIndex: result.oldIndex,
      newIndex: result.newIndex,
      timestamp: result.timestamp,
    });

    const reordered = assignOrderValues<PhotoItem2ViewModel>(result.reorderedItems);
    setReorderablePhotos(reordered);

    if (activeSortCriterion !== 'none') {
      setActiveSortCriterion('none');
    }
  }, [activeSortCriterion]);

  // Compute selected photo for PhotoViewer tab
  const selectedPhoto = useMemo(() => {
    if (photos.length === 0) return null;
    const safeIndex = Math.min(selectedPhotoIndex, photos.length - 1);
    return mapPhotoItem2DataToPhoto(photos[safeIndex]);
  }, [photos, selectedPhotoIndex]);

  const photos2 = useMemo(() => {
    return photos.map(mapPhotoItem2DataToPhoto);
  }, [photos]);

  // Handle loading state
  if (isLoading && dataSourceMode === 'real-10') {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Heading level="h2" className="mb-2">Loading photos...</Heading>
          <Text className="text-muted">Fetching photos from database</Text>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error && dataSourceMode === 'real-10') {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Heading level="h2" className="mb-2 text-danger">Error loading photos</Heading>
          <Text className="text-muted">
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col min-h-0 overflow-hidden">
      {/* Page Title */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <Heading level="h1" className="mb-1">Photo Components Test Page</Heading>
        <Text className="text-muted text-sm">
          Test PhotoCollection2 and PhotoViewer components · Displaying {photos.length} photo{photos.length !== 1 ? 's' : ''}
        </Text>
      </div>

      {/* Split Panel Layout */}
      <SplitPane direction="horizontal" autoSaveId="photo-components-test-layout" className="flex-1">
        {/* LEFT PANEL: Control Panel */}
        <Panel defaultSize={22} minSize={18} maxSize={35} className="flex flex-col">
          <div className="h-full flex flex-col gap-4 p-4 overflow-y-auto bg-surface-1">
            {/* PAGE SETTINGS SECTION */}
            <div className="flex-shrink-0">
              <div className="mb-3 pb-2 border-b border-border">
                <Text className="text-xs font-semibold uppercase tracking-wide text-muted">Page Settings</Text>
              </div>

              {/* Data Source Selector */}
              <div className="space-y-2">
                <Text className="text-sm font-medium">Data Source</Text>
                <div className="flex flex-col gap-1.5">
                  {(['real-10', 'mock-10', 'mock-0', 'mock-500'] as DataSourceMode[]).map((mode) => (
                    <Button
                      key={mode}
                      variant={dataSourceMode === mode ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => setDataSourceMode(mode)}
                      className="w-full justify-start"
                    >
                      {mode === 'real-10' ? 'Real (10 photos)' :
                       mode === 'mock-10' ? 'Mock (10 photos)' :
                       mode === 'mock-0' ? 'Empty (0 photos)' :
                       'Stress Test (500)'}
                    </Button>
                  ))}
                </div>
                <Text className="text-xs text-muted mt-2">Shared across all components</Text>
              </div>
            </div>

            {/* COMPONENT SETTINGS SECTION */}
            <div className="flex-shrink-0">
              <div className="mb-3 pb-2 border-b border-border">
                <Text className="text-xs font-semibold uppercase tracking-wide text-muted">
                  {activeTab === 'collection' ? 'Collection Settings' : 'PhotoViewer Settings'}
                </Text>
              </div>

              {activeTab === 'collection' ? (
                <CollectionSettings
                  layout={layout}
                  itemTemplate={itemTemplate}
                  selectionMode={selectionMode}
                  zoom={zoom}
                  detailLocation={detailLocation}
                  reorderMode={reorderMode}
                  activeSortCriterion={activeSortCriterion}
                  showFixedHeader={showFixedHeader}
                  showScrollHeader={showScrollHeader}
                  showScrollFooter={showScrollFooter}
                  showFixedFooter={showFixedFooter}
                  onLayoutChange={setLayout}
                  onItemTemplateChange={setItemTemplate}
                  onSelectionModeChange={setSelectionMode}
                  onZoomChange={setZoom}
                  onDetailLocationChange={setDetailLocation}
                  onReorderModeChange={setReorderMode}
                  onSortCriterionChange={applySortCriterion}
                  onShowFixedHeaderChange={setShowFixedHeader}
                  onShowScrollHeaderChange={setShowScrollHeader}
                  onShowScrollFooterChange={setShowScrollFooter}
                  onShowFixedFooterChange={setShowFixedFooter}
                />
              ) : (
                <PhotoViewerSettings
                  photos={photos2}
                  selectedIndex={selectedPhotoIndex}
                  onIndexChange={setSelectedPhotoIndex}
                />
              )}
            </div>
          </div>
        </Panel>

        <PanelResizeHandle className="w-1 bg-border hover:bg-primary transition-colors" />

        {/* RIGHT PANEL: Component Display */}
        <Panel defaultSize={78} minSize={65} className="flex flex-col">
          <div className="h-full flex flex-col min-h-0">
            {/* Component Tabs */}
            <div className="flex-shrink-0 flex gap-2 p-4 border-b border-border bg-background">
              <Button
                variant={activeTab === 'collection' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setActiveTab('collection')}
              >
                Collection
              </Button>
              <Button
                variant={activeTab === 'photoviewer' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setActiveTab('photoviewer')}
              >
                PhotoViewer
              </Button>
            </div>

            {/* Component View */}
            <div className="flex-1 min-h-0 p-4 overflow-hidden">
              {activeTab === 'collection' ? (
                <CollectionComponentView
                  photos={reorderablePhotos}
                  layout={layout}
                  itemTemplate={itemTemplate}
                  zoom={zoom}
                  selectionMode={selectionMode}
                  selectedIds={selectedIds}
                  detailLocation={detailLocation}
                  reorderMode={reorderMode}
                  showFixedHeader={showFixedHeader}
                  showScrollHeader={showScrollHeader}
                  showScrollFooter={showScrollFooter}
                  showFixedFooter={showFixedFooter}
                  onSelectionChange={setSelectedIds}
                  onReorder={handleReorder}
                />
              ) : (
                <PhotoViewerComponentView photo={selectedPhoto} />
              )}
            </div>
          </div>
        </Panel>
      </SplitPane>
    </div>
  );
}
